import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, matchPath } from 'react-router-dom';
import { CommandBar as FluentCommandBar } from 'office-ui-fabric-react/lib/CommandBar';
import { TooltipHost } from 'office-ui-fabric-react';
import { CommandBarButton } from 'office-ui-fabric-react/lib/Button';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import last from 'lodash/last';
import csv from 'papaparse';
import { uploadUsers } from '../../actions/users';
import { useSelectContractByAddress } from '../../actions/contracts';
import { can, getImpersonatedUser, exitImpersonatedMode, getUser } from '../../utils/auth';
import { uploadContracts } from '../../actions/digitizedContracts';
import DocumentUpload from '../DocumentUpload';
import { deleteAdjustment } from '../../actions/adjustments';
import styles from './CommandBar.style';
import RemoveAdjustmentDialog from '../RemoveAdjustmentDialog';
import useAPI from '../../hooks/useAPI';
import {
  isAnyLineItemInDraft,
  isCreatorOfAdjustment,
  isRejectedAdjustment,
  isReviewerOfAdjustment,
  isReviewerOfDimensionList,
  parseUserData,
} from '../../utils';
import { AdjustmentStatus } from '../../utils/adjustmentUtils';
import { mandatoryRoles } from '../../constants';
import RemoveAdvanceGroupDialog from '../RemoveAdvanceGroupDialog';
import { deleteAdvanceGroup } from '../../actions/advanceGroups';
import DownloadStatementButton from '../DownloadStatementButton';

const CommandBar = ({ toggleLeftNavCollapse, menuState }) => {
  const history = useHistory();
  const uploadref = useRef(null);
  const uploadUserref = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const pathVariables = location.pathname.split('/');
  const partnerAddress = pathVariables[2];
  const bundleId = pathVariables[2];
  const dimensionListId = pathVariables[2];
  const adjustmentId = pathVariables[2];
  const advancegroupId = pathVariables[2];
  const contractAddress = pathVariables[2];
  const type = pathVariables[3];
  const id = pathVariables[4];
  const slug = pathVariables[3];
  const isAdjustmentsPath = matchPath(location.pathname, {
    path: '/adjustments',
    exact: false,
    strict: false,
  });
  const showAddContractButton = location.pathname === '/contracts' || location.pathname === '/';
  const showAddBundleButton =
    location.pathname === '/products' && location?.state?.tabName === 'bundles';
  const showEditDeleteButtons =
    location.pathname === `/dimensionLists/${partnerAddress}/${type}` &&
    !location.pathname.includes('add') &&
    !location.pathname.includes('edit');
  const showAddListButton = location.pathname === '/dimensionLists';
  const showDeleteRoleButton = matchPath(location.pathname, {
    path: `/permissions/roles/:slug`,
    exact: true,
    strict: false,
  });
  const statementDetailsPath = matchPath(location.pathname, {
    path: '/partner/:partnerAddress/statements/:id',
    exact: true,
    strict: false,
  });
  const bundleDetailsPath = matchPath(location.pathname, {
    path: '/bundles/:bundleId',
    exact: true,
    strict: true,
  });
  const inUserDetailsPage = matchPath(location.pathname, {
    path: '/permissions/users/:id',
    exact: true,
    strict: true,
  });
  const showImpersonateContactsButton = matchPath(location.pathname, {
    path: `/contracts/:contractAddress`,
    exact: true,
    strict: false,
  });
  const advanceGroupDetailsPath = matchPath(location.pathname, {
    path: '/advancegroups/:advancegroupId',
    exact: true,
    strict: true,
  });
  const isContractAdjustmentsPath = matchPath(location.pathname, {
    path: `/contracts/:contractAddress/adjustments`,
    exact: true,
    strict: false,
  });

  const adjustment = useSelector(state => get(state, `entities.adjustments.${adjustmentId}`, null));
  const statement = useSelector(state => get(state, `entities.statementDetail.${id}`, null));
  const statementUrl = get(statement, 'downloadUrls.excelFileUrl', null);
  const payGoStatementUrl = get(statement, 'downloadUrls.payGoExcelFileUrl', null);
  const bundle = useSelector(state =>
    last(
      orderBy(
        Object.values(get(state, `entities.bundle`, [])).filter(data => {
          return data.bundleId === bundleId;
        }),
        ['createdTimeStamp'],
      ),
    ),
  );
  const contract = useSelectContractByAddress(contractAddress);
  const dimensionList = useSelector(state =>
    get(state, `entities.dimensionLists.${dimensionListId}`, { metadata: {} }),
  );
  const user = useSelector(state => get(state, `entities.users.${pathVariables[3]}`, null));

  const [isAdjustmentRemovalRequested, setIsAdjustmentRemovalRequested] = useState();
  const [isAdvanceGroupRemovalRequested, setIsAdvanceGroupRemovalRequested] = useState();
  const handleUpload = files => {
    if (files.length) {
      dispatch(uploadContracts(files));
      history.push('/contracts');
    }
  };

  const onRemoveAdjustmentSuccessCallback = () => {
    setIsAdjustmentRemovalRequested(false);
    history.push('/adjustments');
  };

  const {
    error: removeAdjustmentError,
    isLoading: isRemovingAdjustment,
    fetchApi,
  } = useAPI(() => deleteAdjustment(adjustmentId), [adjustmentId], {
    fetchOnMount: false,
    autoFetch: false,
    onSuccessCallback: onRemoveAdjustmentSuccessCallback,
  });

  const onRemoveAdvanceGroupSuccessCallback = () => {
    setIsAdvanceGroupRemovalRequested(false);
    history.push('/adjustments');
  };

  const {
    error: removeAdvanceGroupError,
    isLoading: isRemovingAdvanceGroup,
    fetchApi: removeAdvanceGroupFetchAPI,
  } = useAPI(() => deleteAdvanceGroup(advancegroupId), [advancegroupId], {
    fetchOnMount: false,
    autoFetch: false,
    onSuccessCallback: onRemoveAdvanceGroupSuccessCallback,
  });

  const handleUploadUser = async files => {
    if (files.length) {
      const { name: fileName } = files[0];
      if (fileName && fileName.endsWith('.csv')) {
        csv.parse(files[0], {
          header: true,
          complete: results => {
            const userData = parseUserData(results);
            dispatch(uploadUsers(userData));
          },
        });
      }
    }
  };

  const handleAdjustmentRemoval = async () => {
    await fetchApi();
  };

  const handleAdvanceGroupRemoval = async () => {
    await removeAdvanceGroupFetchAPI();
  };

  const CommandBarTooltipWrapper = props => {
    if (props.disabled) {
      return (
        <TooltipHost content={<div>{props.tooltipContent} &nbsp;</div>} id="deleteTooltip">
          <CommandBarButton {...props} />
        </TooltipHost>
      );
    }

    return <CommandBarButton {...props} />;
  };

  CommandBarTooltipWrapper.propTypes = {
    disabled: PropTypes.bool.isRequired,
    tooltipContent: PropTypes.string.isRequired,
  };

  const items = [
    {
      key: 'menuCollapse',
      ariaLabel: menuState,
      iconProps: { iconName: 'GlobalNavButton' },
      onClick: toggleLeftNavCollapse,
    },
  ];

  const allLineItemsHaveBeenReversed = adjustmentToCheck =>
    !!adjustmentToCheck.lineItems
      ?.filter(lineItem => !lineItem.reversal)
      .every(lineItem => {
        const foundReversal = adjustmentToCheck.lineItems.find(
          li => li.reversal?.lineItemId === lineItem._id && li.status.toLowerCase() === 'approved',
        );

        return !!foundReversal;
      });

  const canReverseAdjustment = () => {
    if (!adjustment || adjustment.type !== 'AdjustmentToRoyaltiesEarned') {
      return false;
    }

    if (adjustment.status.toLowerCase() !== 'approved') {
      return false;
    }

    if (allLineItemsHaveBeenReversed(adjustment)) {
      return false;
    }

    return !adjustment.lineItems?.some(
      lineItem => lineItem.reversal && lineItem.status.toLowerCase() === 'approvalrequested',
    );
  };

  const canEditAdjustment = () => {
    return (
      !isReviewerOfAdjustment(adjustment) ||
      isCreatorOfAdjustment(adjustment) ||
      isRejectedAdjustment(adjustment)
    );
  };

  const canEditDimensionList = () => {
    return dimensionList?.status !== 'Rejected' || isReviewerOfDimensionList(dimensionList);
  };

  let farItems;
  farItems =
    can('createContract') && showAddContractButton
      ? [
          {
            key: 'addcontract',
            text: 'Add a Contract',
            cacheKey: 'addcontractkey',
            subMenuProps: {
              className: styles.submenu,
              items: [
                {
                  key: 'uploadcontract',
                  text: 'Upload Contract',
                  iconProps: { iconName: 'Upload' },
                  onClick: () => uploadref.current.click(),
                },
                {
                  key: 'addmanually',
                  text: 'Add Manually',
                  iconProps: { iconName: 'PageEdit' },
                  onClick: () => history.push('/add-contract'),
                },
              ],
            },
          },
        ]
      : [];

  if (showImpersonateContactsButton && can('viewUsers') && contract?.partner?.partnerRole) {
    farItems = [
      {
        key: 'partnerContacts',
        text: 'Partner Contacts',
        cacheKey: 'partnerContactsKey',
        iconProps: { iconName: 'People' },
        onClick: () =>
          history.push(
            `/permissions/users?role=partner&selectedPartnerRole=${contract?.partner?.partnerRole}&selectedPartnerName=${contract?.partner?.name}`,
          ),
      },
    ];
  }

  if (getImpersonatedUser()) {
    farItems = [
      {
        key: 'impersonatingPartner',
        text: `Impersonating Partner - ${get(getUser(), 'profile.impersonatedUserName', '')}`,
        cacheKey: 'impersonatingPartner',
        subMenuProps: {
          className: styles.submenu,
          items: [
            {
              key: 'exitImpersonation',
              text: 'Exit Impersonation mode',
              iconProps: { iconName: 'CircleStopSolid' },
              onClick: async () => {
                await exitImpersonatedMode();
                window.location.replace('/');
              },
            },
          ],
        },
      },
    ];
  }

  if (location.pathname === '/permissions/roles' && can('manageRoles')) {
    const createRole = {
      key: 'createRole',
      text: 'Create Role',
      cacheKey: 'createRole',
      iconProps: { iconName: 'Add' },
      ariaLabel: 'create-role',
      onClick: () => history.push('/permissions/add-role'),
    };
    farItems = [createRole];
  }

  if (
    can('manageAdjustments') &&
    (isAdjustmentsPath || isContractAdjustmentsPath) &&
    !location.pathname.includes('copy') &&
    !location.pathname.includes('edit')
  ) {
    const createAdjustmentCommand = {
      key: 'createadjustment',
      text: 'Create Adjustment',
      cacheKey: 'createadjustmentkey',
      iconProps: { iconName: 'Add' },
      onClick: () => history.push({ pathname: '/add-adjustment', contractAddress }),
    };
    const createAdvanceGroupCommand = {
      key: 'createAdvanceGroup',
      text: 'Create Advance Group',
      cacheKey: 'createadvancegroupkey',
      iconProps: { iconName: 'Add' },
      onClick: () => history.push('/add-advancegroup'),
    };
    const deleteAdjustmentCommand = {
      key: 'deleteadjustment',
      text: 'Delete',
      cacheKey: 'deleteadjustmentkey',
      iconProps: { iconName: 'Trash' },
      onClick: () => {
        setIsAdjustmentRemovalRequested(true);
      },
    };
    const copyAdjustmentCommand = {
      key: 'copyadjustment',
      text: 'Copy',
      cacheKey: 'copyadjustmentkey',
      iconProps: { iconName: 'Copy' },
      onClick: () => history.push(`/adjustments/${adjustmentId}/copy`),
    };
    const editAdjustmentCommand = {
      key: 'editadjustment',
      text: 'Edit',
      cacheKey: 'editadjustmentkey',
      iconProps: { iconName: 'PageEdit' },
      onClick: () => {
        if (canReverseAdjustment(adjustment)) {
          history.push(`/adjustments/${adjustmentId}/reverse`);
        } else {
          history.push(`/adjustments/${adjustmentId}/edit`);
        }
      },
    };
    const editAdjustmentCommentsCommand = {
      key: 'editadjustmentcomments',
      text: 'Edit Comments',
      cacheKey: 'editadjustmentcommentskey',
      iconProps: { iconName: 'PageEdit' },
      onClick: () => history.push(`/adjustments/${adjustmentId}/edit-comments`),
    };
    const reverseAdjustmentCommand = {
      key: 'reverseadjustment',
      text: 'Reverse',
      cacheKey: 'reverseadjustmentkey',
      iconProps: { iconName: 'RevToggleKey' },
      onClick: () => history.push(`/adjustments/${adjustmentId}/reverse`),
    };

    if (location.pathname === '/adjustments' || isContractAdjustmentsPath) {
      // Create adjustments page
      farItems = [
        {
          key: 'addAdjustmentOrAdvanceGroup',
          text: 'Create',
          cacheKey: 'addadjustmentoradvancegroupkey',
          subMenuProps: {
            className: styles.submenu,
            items: [createAdjustmentCommand, createAdvanceGroupCommand],
          },
        },
      ];
    } else if (adjustment && adjustment.status.toLowerCase() === 'draft') {
      // Adjustment Details page with adjustment's status=Draft
      farItems = [editAdjustmentCommentsCommand, copyAdjustmentCommand];
      if (canEditAdjustment()) {
        farItems.push(editAdjustmentCommand);
        farItems.push(deleteAdjustmentCommand);
      }
    } else if (isAnyLineItemInDraft(adjustment)) {
      // Adjustment Details page with adjustment's status not in Draft, but a lineItem in Draft
      farItems = [editAdjustmentCommentsCommand, copyAdjustmentCommand];
      if (canEditAdjustment()) {
        farItems.push(editAdjustmentCommand);
      }
    } else if (canReverseAdjustment() && !location.pathname.includes('/reverse')) {
      // Adjustment Details page with status==Approved
      farItems = [editAdjustmentCommentsCommand, copyAdjustmentCommand, reverseAdjustmentCommand];
    } else {
      // Adjustment Details page with status!=Draft
      farItems = [editAdjustmentCommentsCommand, copyAdjustmentCommand];
    }

    if (adjustment && adjustment.type !== 'AdjustmentToRoyaltiesEarned') {
      farItems = farItems.filter(item => item !== editAdjustmentCommentsCommand);
    }

    // Hide copy button for reviewer/approver views
    if (
      adjustment &&
      ((adjustment.status === AdjustmentStatus.ApprovalRequested &&
        getUser()?.profile.oid === adjustment.approver?.approverId) ||
        (adjustment.status === AdjustmentStatus.ReviewRequested &&
          getUser()?.profile.oid === adjustment.approver?.reviewerId))
    ) {
      farItems = farItems.filter(item => item !== copyAdjustmentCommand);
    }
  }

  if (can('manageAdjustments') && advanceGroupDetailsPath && advanceGroupDetailsPath.isExact) {
    const editAdvanceGroupCommand = {
      key: 'editadvancegroup',
      text: 'Edit',
      cacheKey: 'editAdvanceGroups',
      iconProps: { iconName: 'PageEdit' },
      onClick: () => history.push(`/advancegroups/${advancegroupId}/edit`),
    };
    const deleteAdvanceGroupCommand = {
      key: 'deleteadvancegroup',
      text: 'Delete',
      cacheKey: 'deleteadvancegroupkey',
      iconProps: { iconName: 'Trash' },
      onClick: () => {
        setIsAdvanceGroupRemovalRequested(true);
      },
    };

    farItems = [editAdvanceGroupCommand, deleteAdvanceGroupCommand];
  }

  if (location.pathname === '/products' && can('createProduct') && !showAddBundleButton) {
    farItems = [
      {
        key: 'createProduct',
        text: 'Create Product',
        cacheKey: 'createProduct',
        iconProps: { iconName: 'Add' },
        onClick: () => history.push('products/add'),
      },
    ];
  }

  if (location.pathname === '/partners' && can('createPartner')) {
    farItems = [
      {
        key: 'createPartner',
        text: 'Create Partner',
        cacheKey: 'createPartner',
        iconProps: { iconName: 'Add' },
        onClick: () => history.push('partners/add'),
      },
    ];
  }

  if (statementDetailsPath && statementDetailsPath.isExact) {
    const downloadStatementCmd = {
      key: 'downloadStatement',
      text: 'Download Statement',
      role: 'button',
      onRender: () => (
        <DownloadStatementButton
          statementUrl={statementUrl}
          payGoStatementUrl={payGoStatementUrl}
        />
      ),
    };
    farItems = getImpersonatedUser() ? [...farItems, downloadStatementCmd] : [downloadStatementCmd];
  }
  farItems =
    can('manageBundles') && showAddBundleButton
      ? [
          {
            key: 'addBundle',
            text: 'Add Bundle',
            cacheKey: 'addBundleKey',
            subMenuProps: {
              className: styles.submenu,
              items: [
                // {
                //   key: 'uploadCSV',
                //   text: 'Upload .CSV',
                //   iconProps: { iconName: 'Upload' },
                //   onClick: () => uploadref.current.click(),
                // },
                {
                  key: 'addmanually',
                  text: 'Add Manually',
                  iconProps: { iconName: 'PageEdit' },
                  onClick: () => history.push('/add-bundle'),
                },
              ],
            },
          },
        ]
      : farItems;

  if (
    can('manageBundles') &&
    bundleDetailsPath &&
    bundleDetailsPath.isExact &&
    !bundleDetailsPath.url.includes('add') &&
    bundle
  ) {
    farItems = [
      {
        key: 'deleteBundle',
        text: 'Delete Bundle',
        cacheKey: 'deleteBundleKey',
        disabled: !!bundle.transactionCount,
        tooltipContent:
          'Bundle cannot be deleted because there are one or more transactions associated to the Bundle Definition',
        commandBarButtonAs: CommandBarTooltipWrapper,
        className: styles.bundleCommands,
        onClick: () => history.replace({ ...location, state: { delete: true } }),
      },
      {
        key: 'reviseBundle',
        text: 'Revise Bundle',
        cacheKey: 'reviseBundleKey',
        disabled: bundle.locked,
        subMenuProps: {
          className: styles.submenu,
          items: [
            // {
            //   key: 'uploadRev',
            //   text: 'Upload Revision',
            //   iconProps: { iconName: 'Upload' },
            //   onClick: () => uploadref.current.click(),
            // },
            {
              key: 'createRevision',
              text: 'Create Revision',
              iconProps: { iconName: 'PageEdit' },
              onClick: () => history.push(`/bundles/${bundleId}/edit`),
            },
          ],
        },
      },
    ];
  }

  if (can('manageDimensionLists') && showAddListButton) {
    farItems = [
      {
        key: 'createList',
        ariaLabel: 'createList',
        text: 'Create a List',
        onClick: () => history.push('dimensionLists/add'),
      },
    ];
  }

  if (showEditDeleteButtons) {
    farItems = [
      {
        key: 'editlist',
        text: 'Edit',
        iconProps: { iconName: 'Edit' },
        disabled: dimensionList?.status === 'Pending' || !canEditDimensionList(),
        onClick: () => history.push('edit'),
      },
      {
        key: 'deletelist',
        text: 'Delete',
        iconProps: { iconName: 'Delete' },
        disabled:
          dimensionList?.associatedContractDetails?.length !== 0 ||
          dimensionList?.associatedLists?.length !== 0,
        tooltipContent:
          dimensionList?.associatedContractDetails?.length !== 0
            ? 'Cannot delete list associated to active royalty contract'
            : 'Cannot delete list with association to other lists',
        commandBarButtonAs: CommandBarTooltipWrapper,
        onClick: () => history.replace({ ...location, state: { delete: true } }),
      },
    ];
  }

  if (location.pathname === '/permissions/users' && can('createUser')) {
    farItems = [
      {
        key: 'adduser',
        text: 'Add a User',
        cacheKey: 'adduserkey',
        subMenuProps: {
          className: styles.submenu,
          items: [
            // Commenting this option until this option handles error cases
            // {
            //   key: 'uploaduser',
            //   text: 'Upload User Details',
            //   iconProps: { iconName: 'Upload' },
            //   onClick: () => uploadUserref.current.click(),
            // },
            {
              key: 'createUser',
              text: 'Add Manually',
              iconProps: { iconName: 'Add' },
              onClick: () => history.push('/permissions/add-user'),
            },
          ],
        },
      },
    ];
  }

  if (showDeleteRoleButton && can('deleteRoles')) {
    farItems = [
      {
        key: 'deleteRole',
        text: 'Delete',
        iconProps: { iconName: 'Delete' },
        disabled: mandatoryRoles.includes(slug),
        tooltipContent: 'Cannot delete the role',
        commandBarButtonAs: CommandBarTooltipWrapper,
        onClick: () => history.replace({ ...location, state: { delete: true } }),
      },
    ];
  }

  if (inUserDetailsPage && user) {
    farItems = [
      ...(can('impersonatePartner') && user.role === 'partner'
        ? [
            {
              key: 'impersonateUser',
              text: 'Impersonate',
              cacheKey: 'impersonateUserKey',
              iconProps: { iconName: 'RedEye' },
              onClick: () => {
                history.replace({ ...location, state: { impersonate: true } });
              },
            },
          ]
        : []),
      ...(can('deleteUser')
        ? [
            {
              key: 'deleteUser',
              text: 'Delete',
              cacheKey: 'deleteUserKey',
              iconProps: { iconName: 'Trash' },
              onClick: () => history.replace({ ...location, state: { delete: true } }),
            },
          ]
        : []),
    ];
  }
  const dynamicFarItems = farItems.map(item => item.text).join(', ');
  const dynamicAriaLabel = `Command bar with items ${dynamicFarItems}`;

  return (
    <>
      <RemoveAdjustmentDialog
        onDismiss={() => setIsAdjustmentRemovalRequested(false)}
        isOpen={can('manageAdjustments') && isAdjustmentRemovalRequested}
        onSubmit={handleAdjustmentRemoval}
        isRemoving={isRemovingAdjustment}
        adjustmentId={adjustmentId}
        error={removeAdjustmentError}
      />
      <RemoveAdvanceGroupDialog
        onDismiss={() => setIsAdvanceGroupRemovalRequested(false)}
        isOpen={can('manageAdjustments') && isAdvanceGroupRemovalRequested}
        onSubmit={handleAdvanceGroupRemoval}
        isRemoving={isRemovingAdvanceGroup}
        error={removeAdvanceGroupError}
      />
      <FluentCommandBar
        className={styles.root}
        items={items}
        farItems={farItems}
        ariaLabel={dynamicAriaLabel}
        data-testid="commandBarItem"
      />
      <DocumentUpload
        className={styles.uploader}
        ref={uploadUserref}
        onUpload={handleUploadUser}
        accept=".csv"
        id="btn_userUpload"
      >
        Upload Document
      </DocumentUpload>
      <DocumentUpload
        className={styles.uploader}
        ref={uploadref}
        onUpload={handleUpload}
        accept={showAddBundleButton ? '.csv' : '.pdf'}
        id="btn_contractUpload"
      >
        Upload Document
      </DocumentUpload>
    </>
  );
};

CommandBar.propTypes = {
  toggleLeftNavCollapse: PropTypes.func.isRequired,
  menuState: PropTypes.string,
};
CommandBar.defaultProps = {
  menuState: 'menu',
};

export default CommandBar;
